import React, { useState, useEffect } from 'react';
import { AlertCircle, Circle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui';
import { fetchAvailableStaff, createAssignment } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

function getWorkloadColor(level) {
  switch (level) {
    case 'critical': return '#D94F4F';
    case 'red': return '#D94F4F';
    case 'yellow': return '#E5A820';
    default: return '#2EB87A';
  }
}

function getWorkloadIcon(level) {
  const color = getWorkloadColor(level);
  const size = 14;
  if (level === 'critical') return <AlertCircle size={size} color={color} strokeWidth={2} />;
  return <Circle size={size} color={color} fill={color} strokeWidth={2} />;
}


function AssignmentModal({ item, currentUserId, currentUserName, currentUserRole, onClose, onAssign }) {
  const { getToken } = useAuth();
  const [assignTo, setAssignTo] = useState('');
  const [dueDate, setDueDate] = useState(item?.dueDate ? item.dueDate.split('T')[0] : '');
  const [priority, setPriority] = useState(item?.priority || 'medium');
  const [justification, setJustification] = useState('');
  const [estimatedTime, setEstimatedTime] = useState(item?.estimatedTime || 60);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [workloads, setWorkloads] = useState({});
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    // Fetch staff from API; return empty list if none available
    fetchAvailableStaff(null, getToken)
      .then((apiStaff) => {
        const users = (Array.isArray(apiStaff) && apiStaff.length > 0)
          ? apiStaff.map((s) => ({
              id: s.id,
              name: s.label || s.name || s.id,
              role: s.role || 'staff',
              unit: s.unit || '',
            }))
          : [];
        setAvailableUsers(users.filter(u => u.id !== currentUserId));

        // Workload data will come from backend in future; for now set empty
        const loads = {};
        users.forEach(u => {
          if (u.id !== currentUserId) {
            loads[u.id] = { count: 0, points: 0, level: 'green' };
          }
        });
        setWorkloads(loads);
      })
      .catch(() => {
        setAvailableUsers([]);
        setWorkloads({});
      });
  }, [currentUserId, getToken]);

  useEffect(() => {
    if (assignTo && workloads[assignTo]) {
      const workload = workloads[assignTo];
      if (workload.level === 'red' || workload.level === 'critical') {
        setWarning({
          level: workload.level,
          count: workload.count,
          message: `${availableUsers.find(u => u.id === assignTo)?.name} is currently ${workload.level === 'critical' ? 'critically' : 'heavily'} overloaded (${workload.count} assignments). Consider assigning to someone with lower workload.`
        });
      } else {
        setWarning(null);
      }
    } else {
      setWarning(null);
    }
  }, [assignTo, workloads, availableUsers]);

  const handleAssign = () => {
    if (!assignTo || !dueDate || !priority) {
      alert('Please fill in all required fields (Assign To, Due Date, Priority)');
      return;
    }

    const assignment = {
      itemType: item?.type || 'priorityQueueTask',
      itemId: item?.id,
      itemTitle: item?.title || item?.name || 'Untitled Task',
      assignedBy: currentUserId,
      assignedByName: currentUserName,
      assignedTo: assignTo,
      assignedToName: availableUsers.find(u => u.id === assignTo)?.name || 'Unknown',
      dueDate: new Date(dueDate).toISOString(),
      priority,
      justification: justification || null,
      estimatedTime: parseInt(estimatedTime) || 60,
      status: 'active',
      workloadPoints: 5
    };

    // Optimistic: close immediately, create in background
    onAssign?.(assignment);
    onClose();
    createAssignment(assignment, getToken).catch(err => {
      console.error('Failed to create assignment:', err);
    });
    
    // Show success message
    if (typeof window !== 'undefined') {
      const msg = document.createElement('div');
      msg.textContent = `Task assigned to ${assignment.assignedToName}`;
      msg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10B981;color:white;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(msg);
      setTimeout(() => msg.remove(), 2500);
    }
  };

  const selectedUser = availableUsers.find(u => u.id === assignTo);
  const selectedWorkload = assignTo ? workloads[assignTo] : null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{
          background: 'linear-gradient(135deg, #00CED1 0%, #2C5FA8 100%)',
          color: 'white',
          padding: '20px 30px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 className="text-2xl" style={{ fontWeight: 300, margin: 0 }}>Assign Task</h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} style={{
            width: 32,
            height: 32,
            borderRadius: 4,
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }} aria-label="Close">✕</Button>
        </div>

        <div className="modal-body" style={{ padding: '30px' }}>
          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Task
            </label>
            <div className="text-body" style={{ padding: '12px', background: '#F9FAFB', borderRadius: 6 }}>
              {item?.title || item?.name || 'Untitled Task'}
            </div>
            {item?.status && (
              <div className="text-body-sm text-text-muted" style={{ marginTop: 8 }}>
                Current Status: <span className="font-semibold">{item.status}</span>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Assign To <span className="text-red-critical">*</span>
            </label>
            <select
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontFamily: 'inherit',
                fontSize: 14
              }}
            >
              <option value="">Select Person</option>
              {availableUsers.map(user => {
                const workload = workloads[user.id];
                const icon = workload ? getWorkloadIcon(workload.level) : getWorkloadIcon('green');
                const count = workload?.count || 0;
                return (
                  <option key={user.id} value={user.id}>
                    {icon} {user.name} ({count} assignments)
                  </option>
                );
              })}
            </select>
            {selectedUser && selectedWorkload && (
              <div className={`text-body-sm ${selectedWorkload.level === 'green' ? 'text-green-nn' : selectedWorkload.level === 'yellow' ? 'text-yellow-nn' : 'text-red-critical'}`} style={{
                marginTop: 8,
                padding: '8px 12px',
                background: selectedWorkload.level === 'green' ? '#E8F5E9' : selectedWorkload.level === 'yellow' ? '#FEF3C7' : '#FEE2E2',
                borderRadius: 6,
              }}>
                {selectedUser.name}: {selectedWorkload.count} active assignments ({selectedWorkload.level})
              </div>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Due Date <span className="text-red-critical">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontFamily: 'inherit',
                fontSize: 14
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Priority <span className="text-red-critical">*</span>
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontFamily: 'inherit',
                fontSize: 14
              }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Estimated Time (minutes)
            </label>
            <input
              type="number"
              value={estimatedTime}
              onChange={(e) => setEstimatedTime(e.target.value)}
              min="1"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontFamily: 'inherit',
                fontSize: 14
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="text-body font-medium text-text-dark" style={{ display: 'block', marginBottom: 5 }}>
              Reason (optional)
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explain why this task is being assigned..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontFamily: 'inherit',
                fontSize: 14,
                resize: 'vertical'
              }}
            />
          </div>

          {warning && (
            <div style={{
              marginBottom: 20,
              padding: '12px',
              background: warning.level === 'critical' ? '#FEE2E2' : '#FEF3C7',
              borderLeft: `4px solid ${getWorkloadColor(warning.level)}`,
              borderRadius: 6
            }}>
              <div className={`text-body font-semibold ${warning.level === 'critical' ? 'text-red-critical' : 'text-yellow-nn'}`} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={16} strokeWidth={2} />
                {warning.level === 'critical' ? 'Critical Overload Warning' : 'Overload Warning'}
              </div>
              <div className={`text-body ${warning.level === 'critical' ? 'text-red-critical' : 'text-yellow-nn'}`}>
                {warning.message}
              </div>
              {availableUsers.filter(u => {
                const w = workloads[u.id];
                return w && (w.level === 'green' || w.level === 'yellow');
              }).length > 0 && (
                <div className="text-body-sm text-text-muted" style={{ marginTop: 8 }}>
                  Consider: {availableUsers
                    .filter(u => {
                      const w = workloads[u.id];
                      return w && (w.level === 'green' || w.level === 'yellow');
                    })
                    .slice(0, 3)
                    .map(u => u.name)
                    .join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          padding: '20px 30px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10
        }}>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="btn-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleAssign}
          >
            {warning && (warning.level === 'red' || warning.level === 'critical') ? 'Assign Anyway' : 'Assign'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AssignmentModal;
